import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  Link
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  WhatsApp as WhatsAppIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';

interface PasswordResetLinkDialogProps {
  open: boolean;
  onClose: () => void;
  employeeName: string;
  employeeEmail: string;
  resetLink: string;
}

const PasswordResetLinkDialog: React.FC<PasswordResetLinkDialogProps> = ({
  open,
  onClose,
  employeeName,
  employeeEmail,
  resetLink
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(resetLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copiando enlace:', err);
    }
  };

  const handleOpenLink = () => {
    window.open(resetLink, '_blank');
  };

  const handleWhatsAppShare = () => {
    const message = `Hola ${employeeName}, te envío el enlace para restablecer tu contraseña en Vigilantium Learning:\n\n${resetLink}\n\nEste enlace es válido por 1 hora. Si tienes alguna pregunta, no dudes en contactarme.`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <LockResetIcon color="warning" />
          <Typography variant="h6">
            Enlace de Recuperación de Contraseña
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Información del empleado */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Empleado:</strong> {employeeName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Email:</strong> {employeeEmail}
            </Typography>
          </Box>

          <Divider />

          {/* Alerta informativa */}
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Importante:</strong> Este enlace es válido por <strong>1 hora</strong>. 
              Puedes copiarlo para enviarlo por WhatsApp o hacer clic para abrirlo directamente.
            </Typography>
          </Alert>

          {/* Enlace de recuperación */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Enlace de Recuperación:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                value={resetLink}
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                  sx: { 
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }
                }}
              />
              <Tooltip title={copied ? "¡Copiado!" : "Copiar enlace"}>
                <IconButton 
                  onClick={handleCopyLink}
                  color={copied ? "success" : "primary"}
                >
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Botones de acción */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<OpenIcon />}
              onClick={handleOpenLink}
              color="primary"
            >
              Abrir Enlace
            </Button>
            <Button
              variant="contained"
              startIcon={<WhatsAppIcon />}
              onClick={handleWhatsAppShare}
              sx={{ 
                backgroundColor: '#25D366',
                '&:hover': {
                  backgroundColor: '#128C7E'
                }
              }}
            >
              Enviar por WhatsApp
            </Button>
          </Box>

          {/* Instrucciones adicionales */}
          <Box sx={{ 
            backgroundColor: 'grey.50', 
            p: 2, 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Typography variant="subtitle2" gutterBottom>
              Instrucciones para el empleado:
            </Typography>
            <Typography variant="body2" component="div">
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Hacer clic en el enlace o copiarlo en el navegador</li>
                <li>Crear una nueva contraseña segura</li>
                <li>Confirmar la nueva contraseña</li>
                <li>Iniciar sesión con las nuevas credenciales</li>
              </ol>
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordResetLinkDialog;
